import { useMemo } from 'react';

import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

import {
  EditEntitySection,
  isEditEntitySection,
} from '../EditEntitySplitScreen.types';

// The edit-entity trowser is fully URL-driven: which entity (`target`) and which
// subform (`section`) both live in the URL, so there's no transient open data.
// Declaration-merged so `useTrowser(...).section` is typed to EditEntitySection
// without the navigation layer importing feature types.
declare module '@/modules/navigation/trowsers/trowser.types' {
  interface TrowserDataMap {
    [TrowserKey.EditEntity]: {
      // The URL-addressable section (which subform tab); optional because the
      // trowser opens at Basic Information by default.
      section?: EditEntitySection;
    };
  }
}

export interface EditEntityTrowser {
  /** Whether the edit-entity trowser is open (for any entity). */
  isOpen: boolean;
  /** The entity id the trowser is open for (from the URL), if any. */
  entityId: string | undefined;
  /** The trowser's current section (from the URL), if valid. */
  section: EditEntitySection | null;
  /** Whether the trowser is open specifically for `entityId`. */
  isOpenForEntity: (entityId: string) => boolean;
  /** Open the trowser for a specific entity, optionally at a section. */
  openForEntity: (
    entityId: string,
    options?: { section?: EditEntitySection }
  ) => void;
  /** Close the trowser. */
  close: () => void;
  /** Change the trowser's section without adding a history entry. */
  setSection: (section: EditEntitySection) => void;
}

/**
 * Thin, typed accessor over the global {@link useTrowser} state for the
 * edit-entity trowser ({@link TrowserKey.EditEntity}). Everything the trowser
 * needs (which entity, which section) lives in the URL; the entity type is
 * derived from the loaded entity by each host.
 */
export function useEditEntityTrowser(): EditEntityTrowser {
  const { isOpen, target, section, open, close, setSection } = useTrowser(
    TrowserKey.EditEntity
  );

  return useMemo(
    () => ({
      isOpen,
      entityId: target,
      section: isEditEntitySection(section) ? section : null,
      isOpenForEntity: (entityId: string) =>
        isOpen && (!target || target === entityId),
      openForEntity: (entityId, options) =>
        open({ target: entityId, section: options?.section }),
      close,
      setSection,
    }),
    [isOpen, target, section, open, close, setSection]
  );
}
